import { useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Heading,
  Separator,
  Text,
} from "@radix-ui/themes";
import { Car, CheckCircle2, Users, UserX } from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface TeamParticipant {
  id: number;
  slug: string;
  name: string;
  location: string | null;
}

interface TeamInfo {
  description: string;
  logo_url: string | null;
}

interface RosterMember {
  user_id: number;
  email: string;
  is_owner: boolean;
  driver_car: { id: number; display_name: string } | null;
}

interface RecentEvent {
  id: number;
  status: string;
  event_name: string;
  event_date: string | null;
  attended: boolean;
}

interface MemberProps {
  view: "member";
  participant: TeamParticipant;
  team: TeamInfo;
  my_car_id: number | null;
  my_cars: { id: number; display_name: string }[];
  roster: RosterMember[];
  recent_events: RecentEvent[];
}

interface InvitedProps {
  view: "invited";
  invitation_id: number;
  participant: TeamParticipant;
  team: TeamInfo;
  roster: RosterMember[];
}

interface PublicProps {
  view: "public";
  participant: TeamParticipant;
  team: TeamInfo;
  roster: { display_name: string }[];
  events_attended: { event_name: string; event_date: string | null }[];
}

type Props = MemberProps | InvitedProps | PublicProps;

// ── Shared helpers ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: "10.5px",
      fontWeight: 500,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "var(--gray-9)",
      marginBottom: "0.75rem",
    }}>
      {children}
    </p>
  );
}

function TeamHero({
  participant,
  team,
  badge,
}: {
  participant: TeamParticipant;
  team: TeamInfo;
  badge: { label: string; color: "green" | "amber" | "gray" | "pink" };
}) {
  return (
    <Card style={{ marginBottom: "1.5rem" }}>
      <Flex gap="4" align="start">
        <Avatar
          size="6"
          src={team.logo_url ?? undefined}
          fallback={participant.name[0].toUpperCase()}
          radius="full"
        />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Flex align="center" gap="2" mb="1">
            <Heading size="6">{participant.name}</Heading>
            <Badge size="1" variant="soft" color={badge.color} radius="full">
              {badge.label}
            </Badge>
          </Flex>
          {participant.location && (
            <Text size="2" color="gray">{participant.location}</Text>
          )}
          {team.description && (
            <Text as="p" size="2" color="gray" mt="3">{team.description}</Text>
          )}
        </Box>
      </Flex>
    </Card>
  );
}

function ReadOnlyRoster({ roster }: { roster: RosterMember[] }) {
  return (
    <Card>
      {roster.length === 0 ? (
        <Text size="2" color="gray">No members yet.</Text>
      ) : (
        roster.map((member, i) => (
          <Flex
            key={member.user_id}
            align="center"
            gap="2"
            py="3"
            style={{
              borderBottom: i < roster.length - 1 ? "1px solid var(--gray-4)" : "none",
            }}
          >
            <Avatar
              size="2"
              fallback={member.email[0].toUpperCase()}
              color={member.is_owner ? undefined : "gray"}
              radius="full"
            />
            <Box style={{ minWidth: 0 }}>
              <Flex align="center" gap="2">
                <Text size="2" weight="medium">{member.email}</Text>
                {member.is_owner && (
                  <Badge size="1" variant="soft" radius="full">Owner</Badge>
                )}
              </Flex>
              {member.driver_car && (
                <Flex align="center" gap="1" mt="1">
                  <Car size={11} color="var(--gray-9)" />
                  <Text size="1" color="gray">{member.driver_car.display_name}</Text>
                </Flex>
              )}
            </Box>
          </Flex>
        ))
      )}
    </Card>
  );
}

// ── Member view ───────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  pending:   "Under review",
  approved:  "Accepted",
  rejected:  "Not accepted",
  withdrawn: "Withdrawn",
  draft:     "Draft",
};

const STATUS_COLORS: Record<string, "green" | "indigo" | "crimson" | "gray"> = {
  approved:  "green",
  pending:   "indigo",
  rejected:  "crimson",
  withdrawn: "gray",
  draft:     "gray",
};

function MemberView({ participant, team, my_car_id, my_cars, roster, recent_events }: MemberProps) {
  const [selectedCarId, setSelectedCarId] = useState<number | null>(my_car_id);
  const [saving, setSaving] = useState(false);

  function saveCarSelection() {
    setSaving(true);
    router.patch(
      `/teams/${participant.slug}/car`,
      { driver_car_id: selectedCarId },
      { onFinish: () => setSaving(false) }
    );
  }

  function handleLeave() {
    if (confirm(`Leave ${participant.name}? You will be removed from the roster.`)) {
      router.delete(`/teams/${participant.slug}/leave`);
    }
  }

  return (
    <div className="flex">
      <Head title={participant.name} />
      <div className="grow">
        <main style={{ maxWidth: 900, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>

          <TeamHero participant={participant} team={team} badge={{ label: "Member", color: "green" }} />

          {/* ── My Participation ── */}
          <section style={{ marginBottom: "2rem" }}>
            <SectionLabel>My Participation</SectionLabel>
            <Card>
              <Text as="p" size="2" weight="medium" mb="3">
                Car for this team
              </Text>
              <Text as="p" size="2" color="gray" mb="4">
                Select which car you're running under this team. This appears on the team roster.
              </Text>

              {my_cars.length === 0 ? (
                <Callout.Root color="gray" variant="surface" mb="4">
                  <Callout.Icon><Car size={16} /></Callout.Icon>
                  <Callout.Text>
                    You don't have any cars in your garage yet.{" "}
                    <Link href="/driver_cars/new" style={{ color: "var(--accent-11)" }}>
                      Add a car →
                    </Link>
                  </Callout.Text>
                </Callout.Root>
              ) : (
                <Flex direction="column" gap="2" mb="4">
                  {my_cars.map((car) => (
                    <label
                      key={car.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "0.75rem 1rem",
                        borderRadius: "var(--radius-3)",
                        border: selectedCarId === car.id
                          ? "1.5px solid var(--accent-8)"
                          : "1px solid var(--gray-5)",
                        background: selectedCarId === car.id ? "var(--accent-2)" : "var(--color-panel-solid)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="car"
                        value={car.id}
                        checked={selectedCarId === car.id}
                        onChange={() => setSelectedCarId(car.id)}
                        style={{ accentColor: "var(--accent-9)" }}
                      />
                      <Flex align="center" gap="2">
                        <Car size={14} color="var(--gray-9)" />
                        <Text size="2">{car.display_name}</Text>
                      </Flex>
                    </label>
                  ))}
                  {selectedCarId !== null && (
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "0.75rem 1rem",
                        borderRadius: "var(--radius-3)",
                        border: "1px solid var(--gray-5)",
                        background: "var(--color-panel-solid)",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="car"
                        value=""
                        checked={selectedCarId === null}
                        onChange={() => setSelectedCarId(null)}
                        style={{ accentColor: "var(--accent-9)" }}
                      />
                      <Text size="2" color="gray">No car (unset)</Text>
                    </label>
                  )}
                </Flex>
              )}

              <Flex justify="between" align="center">
                <Button
                  variant="soft"
                  color="red"
                  onClick={handleLeave}
                >
                  <UserX size={14} /> Leave team
                </Button>
                {my_cars.length > 0 && (
                  <Button onClick={saveCarSelection} disabled={saving}>
                    <CheckCircle2 size={14} /> Save
                  </Button>
                )}
              </Flex>
            </Card>
          </section>

          {/* ── Team Roster ── */}
          <section style={{ marginBottom: "2rem" }}>
            <SectionLabel>Team Roster</SectionLabel>
            <ReadOnlyRoster roster={roster} />
          </section>

          {/* ── Recent Events ── */}
          {recent_events.length > 0 && (
            <section>
              <SectionLabel>Recent Events</SectionLabel>
              <Card>
                {recent_events.map((item, i) => (
                  <Flex
                    key={item.id}
                    align="center"
                    justify="between"
                    gap="3"
                    py="3"
                    style={{
                      borderBottom: i < recent_events.length - 1 ? "1px solid var(--gray-4)" : "none",
                    }}
                  >
                    <Box>
                      <Text size="2" weight="medium">{item.event_name}</Text>
                      {item.event_date && (
                        <Text as="p" size="1" color="gray">{item.event_date}</Text>
                      )}
                    </Box>
                    <Badge
                      size="1"
                      variant="soft"
                      color={item.attended ? "green" : STATUS_COLORS[item.status] ?? "gray"}
                      radius="full"
                    >
                      {item.attended ? "Attended" : STATUS_LABELS[item.status] ?? item.status}
                    </Badge>
                  </Flex>
                ))}
              </Card>
            </section>
          )}

        </main>
      </div>
    </div>
  );
}

// ── Invited view ──────────────────────────────────────────────────────────────

function InvitedView({ invitation_id, participant, team, roster }: InvitedProps) {
  function accept() {
    router.patch(`/team_invitations/${invitation_id}/accept`);
  }

  function decline() {
    if (confirm("Are you sure you want to decline this invitation?")) {
      router.delete(`/team_invitations/${invitation_id}`);
    }
  }

  return (
    <div className="flex">
      <Head title={`Invitation — ${participant.name}`} />
      <div className="grow">
        <main style={{ maxWidth: 700, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>

          <TeamHero participant={participant} team={team} badge={{ label: "Invited", color: "amber" }} />

          {/* ── Accept / Decline callout ── */}
          <section style={{ marginBottom: "2rem" }}>
            <Callout.Root variant="surface" mb="4">
              <Callout.Icon><Users size={16} /></Callout.Icon>
              <Callout.Text>
                You've been invited to join <strong>{participant.name}</strong>.
                Accepting means you'll appear on the roster and can be listed on team event applications.
                You can leave the team at any time.
              </Callout.Text>
            </Callout.Root>
            <Flex direction="column" gap="2">
              <Button size="3" onClick={accept} style={{ width: "100%" }}>
                Accept invitation
              </Button>
              <Button size="3" variant="soft" color="gray" onClick={decline} style={{ width: "100%" }}>
                Decline
              </Button>
            </Flex>
          </section>

          <Separator size="4" mb="5" />

          {/* ── Roster preview ── */}
          <section>
            <SectionLabel>Team Roster</SectionLabel>
            <ReadOnlyRoster roster={roster} />
          </section>

        </main>
      </div>
    </div>
  );
}

// ── Public view ───────────────────────────────────────────────────────────────

function PublicView({ participant, team, roster, events_attended }: PublicProps) {
  return (
    <>
      <Head title={participant.name} />
      <main style={{ maxWidth: 700, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>

        <TeamHero participant={participant} team={team} badge={{ label: "Team", color: "gray" }} />

        {/* ── Roster ── */}
        <section style={{ marginBottom: "2rem" }}>
          <SectionLabel>Roster</SectionLabel>
          <Card>
            {roster.length === 0 ? (
              <Text size="2" color="gray">No public roster.</Text>
            ) : (
              roster.map((member, i) => (
                <Flex
                  key={i}
                  align="center"
                  gap="2"
                  py="3"
                  style={{
                    borderBottom: i < roster.length - 1 ? "1px solid var(--gray-4)" : "none",
                  }}
                >
                  <Avatar
                    size="2"
                    fallback={member.display_name[0].toUpperCase()}
                    color="gray"
                    radius="full"
                  />
                  <Text size="2">{member.display_name}</Text>
                </Flex>
              ))
            )}
          </Card>
        </section>

        {/* ── Events Attended ── */}
        {events_attended.length > 0 && (
          <section style={{ marginBottom: "2rem" }}>
            <SectionLabel>Events Attended</SectionLabel>
            <Card>
              {events_attended.map((item, i) => (
                <Flex
                  key={i}
                  align="center"
                  justify="between"
                  gap="3"
                  py="3"
                  style={{
                    borderBottom: i < events_attended.length - 1 ? "1px solid var(--gray-4)" : "none",
                  }}
                >
                  <Text size="2" weight="medium">{item.event_name}</Text>
                  {item.event_date && (
                    <Text size="1" color="gray">{item.event_date}</Text>
                  )}
                </Flex>
              ))}
            </Card>
          </section>
        )}

        {/* ── Sign-up CTA ── */}
        <div style={{
          background: "var(--gray-2)",
          border: "0.5px solid var(--gray-5)",
          borderRadius: 12,
          padding: "1.5rem",
          textAlign: "center",
        }}>
          <Text as="p" size="3" weight="medium" mb="2">
            Want to join events like this?
          </Text>
          <Text as="p" size="2" color="gray" mb="4">
            Sign in with your email to apply for events and connect with teams. It's free.
          </Text>
          {/* A Devise route, so a plain anchor. */}
          <Button variant="solid" asChild>
            <a href="/users/sign_in">Sign in →</a>
          </Button>
        </div>

      </main>
    </>
  );
}

// ── Root export ───────────────────────────────────────────────────────────────

export default function TeamShow(props: Props) {
  if (props.view === "member")  return <MemberView  {...props} />;
  if (props.view === "invited") return <InvitedView {...props} />;
  return <PublicView {...props} />;
}
